import { Injectable } from '@angular/core';
import { isNullOrUndefined, MessageService } from '@sla-ng/core';

import { FieldType, IField } from '@sla-ng/ui-material';
import * as moment from 'moment';
import { BehaviorSubject, firstValueFrom, Observable, of } from 'rxjs';
import { ICostAccountingType } from '../../../models/cost-accounting-type.model';
import { ICostCenterCostUnitDistribution } from '../../../models/cost-center-cost-unit-distribution.model';
import { ICostCenter } from '../../../models/cost-center.model';
import { ICostUnitDistributionTransfer } from '../../../models/cost-unit-distribution-transfer.model';
import { ICostUnit } from '../../../models/cost-unit.model';
import { IEditCostCenterCostUnitDistribution } from '../../../models/edit-cost-center-cost-unit-distribution.model';
import { IReference } from '../../../models/reference.model';
import { CostAccountingTypeResource } from '../../../resources/cost-accounting-type.resource';
import { CostCenterResource } from '../../../resources/cost-center.resource';
import { CostUnitResource } from '../../../resources/cost-unit.resource';
import { ReferenceResource } from '../../../resources/reference.resource';
import { IJoinCostCenterCostUnitState } from './join-cost-center-cost-unit-state.model';

let _state: IJoinCostCenterCostUnitState = {
    selectedDistributionReference: null,
    dialogEditFields: [],
    selectedCostAccountingType: null,
    selectedValidFrom: null,
    selectedValidTo: null,
    assignmentColumns: [ 'costUnitName', 'costUnitArticleNr', 'placeholder', 'validFrom', 'validTo', 'calculationOrder', 'actions' ],
    assignments: [],
    costUnits: [],
    dialogAddFields: [],
    isLoading: false,
    references: [],
    selectFields: [],
    selectedCostCenter: null,
};

@Injectable({
    providedIn: 'root',
})
export class JoinCostCenterCostUnitFacade {

    protected store$: BehaviorSubject<IJoinCostCenterCostUnitState> = new BehaviorSubject<IJoinCostCenterCostUnitState>(_state);

    constructor(private myCostCenterResource: CostCenterResource,
                private myCostAccountingTypeResource: CostAccountingTypeResource,
                private myReferenceResource: ReferenceResource,
                private myCostUnitResource: CostUnitResource,
                private myMessageService: MessageService) {
    }

    public getStateSnapshot(): IJoinCostCenterCostUnitState {
        return this.store$.value;
    }

    public clearState(): void {
        this.updateState({
            selectedDistributionReference: null,
            dialogEditFields: [],
            selectedCostAccountingType: null,
            selectedValidFrom: null,
            selectedValidTo: null,
            assignmentColumns: [ 'costUnitName', 'costUnitArticleNr', 'placeholder', 'validFrom', 'validTo', 'actions' ],
            assignments: [],
            costUnits: [],
            dialogAddFields: [],
            isLoading: false,
            references: [],
            selectFields: [],
            selectedCostCenter: null,
        });
    }

    public async fetchData(): Promise<void> {
        this.setIsloading(true);

        // Load data concurrently
        const [ references, costUnits, costCenters, costAccountingTypes ]: [ IReference[], ICostUnit[], ICostCenter[], ICostAccountingType[] ] = await Promise.all([
            firstValueFrom(this.myReferenceResource.getAllReferences()).catch(() => []),
            firstValueFrom(this.myCostUnitResource.getAllCostUnits()).catch(() => []),
            firstValueFrom(this.myCostCenterResource.getCostCenters()).catch(() => []),
            firstValueFrom(this.myCostAccountingTypeResource.getAllCostAccountingTypes()).catch(() => []),
        ]);

        this.updateState({ ..._state, costUnits });
        this.updateState({ ..._state, references });
        this.setInputFields(references, costCenters, costUnits, costAccountingTypes);
        this.setIsloading(false);
    }

    public async setSelectedKostenstelle(selectedCostCenter: number): Promise<void> {
        this.updateState({ ..._state, selectedCostCenter });
        await Promise.all([ this.fetchAssignmentsIfFiltersAreValid(), this.fetchDistributionReference() ]);
    }

    public async setSelectedCostAccountingType(selectedCostAccountingType: number): Promise<void> {
        this.updateState({ ..._state, selectedCostAccountingType });
        await Promise.all([ this.fetchAssignmentsIfFiltersAreValid(), this.fetchDistributionReference() ]);
    }

    public async transferCostCenterCostUnit(request: ICostUnitDistributionTransfer): Promise<void> {
        try {
            this.setIsloading(true);
            await firstValueFrom(this.myCostCenterResource.transferCostCenterCostUnitDistribution(request));
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-center-cost-unit-distribution.actions.transferred.label');
        } catch (e) {
            console.error(e);
        }
        this.setIsloading(false);
    }

    public async setSelectedDaterange(selectedValidFrom: moment.Moment, selectedValidTo: moment.Moment): Promise<void> {
        this.updateState({ ..._state, selectedValidFrom });
        this.updateState({ ..._state, selectedValidTo });
        await this.fetchAssignmentsIfFiltersAreValid();
    }

    public async addAssignment(data: IEditCostCenterCostUnitDistribution): Promise<void> {
        const state: IJoinCostCenterCostUnitState = this.getStateSnapshot();
        this.setIsloading(true);
        const newAssignment: ICostCenterCostUnitDistribution = await this.myCostCenterResource.addCostCenterCostUnitDistribution(
            state.selectedCostCenter,
            data.costUnitId,
            data.costAccountingTypeId,
            data.validFrom,
            data.validTo,
        ).toPromise().catch(() => null);
        if (newAssignment !== null) {
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-center-cost-unit-distribution.actions.added.label');
        }
        this.setIsloading(false);
    }

    public async updateDistributionValidity(distributionId: number, validTo: moment.Moment): Promise<void> {
        const state: IJoinCostCenterCostUnitState = this.getStateSnapshot();
        this.setIsloading(true);
        const updatedAssignment: ICostCenterCostUnitDistribution = await this.myCostCenterResource
            .updateCostCenterCostUnitDistributionValidity(state.selectedCostCenter, distributionId, validTo).toPromise().catch(() => null);
        if (updatedAssignment !== null) {
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-center-cost-unit-distribution.actions.edited.label');
        }

        this.setIsloading(false);
    }

    public async updateCalculationOrder(distributionId: number, calculationOrder: number): Promise<void> {
        this.setIsloading(true);
        const updatedAssignment: ICostCenterCostUnitDistribution = await firstValueFrom(this.myCostCenterResource
            .updateCostCenterCostUnitDistributionOrder(_state.selectedCostCenter, distributionId, calculationOrder)).catch(() => null);

        if (updatedAssignment !== null) {
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-center-cost-unit-distribution.actions.edited.label');
            const index = _state.assignments.findIndex(d => d.id === distributionId);
            _state.assignments[ index ].calculationOrder = updatedAssignment.calculationOrder;
            this.updateState({ ..._state, assignments: [ ..._state.assignments ] });
        }
        this.setIsloading(false);
    }

    public async fetchAssignmentsIfFiltersAreValid(): Promise<void> {
        const state: IJoinCostCenterCostUnitState = this.getStateSnapshot();
        if (!isNullOrUndefined(state.selectedCostCenter)
            && !isNullOrUndefined(state.selectedCostAccountingType)
            && !isNullOrUndefined(state.selectedValidFrom)
            && !isNullOrUndefined(state.selectedValidTo)) {
            this.setIsloading(true);
            const distributions: ICostCenterCostUnitDistribution[] = await this.myCostCenterResource
                .getCostUnitDistributions(state.selectedCostCenter, state.selectedCostAccountingType, state.selectedValidFrom, state.selectedValidTo).toPromise().catch(() => []);
            this.updateState({ ..._state, assignments: distributions });
            this.setIsloading(false);
        }

    }

    public async deleteDistribution(distribution: ICostCenterCostUnitDistribution): Promise<void> {
        const state: IJoinCostCenterCostUnitState = this.getStateSnapshot();
        this.setIsloading(true);
        try {
            await this.myCostCenterResource.deleteCostCenterCostUnitDistribution(state.selectedCostCenter, distribution.id).toPromise().catch(() => null);
            this.myMessageService.successTrans('app.actions.feedback.deleted', 'app.page.cost-center-cost-unit-distribution.actions.deleted.label');
        } catch (e) {
            console.error(e);
        }
        this.setIsloading(false);
    }

    public async setSelectedDistributionReference(distributionReferenceId: number): Promise<void> {
        const state: IJoinCostCenterCostUnitState = this.getStateSnapshot();
        this.setIsloading(true);
        try {
            await this.myCostCenterResource.updateCostCenterCostUnitDistributionReference(state.selectedCostCenter, distributionReferenceId, state.selectedCostAccountingType).toPromise();
            this.updateState({ ..._state, selectedDistributionReference: distributionReferenceId });
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-center-cost-unit-distribution-reference.actions.edited.label');
        } catch {
            this.updateState({ ..._state, selectedDistributionReference: null });
        }
        this.setIsloading(false);
    }

    public subState(): Observable<IJoinCostCenterCostUnitState> {
        return this.store$.asObservable();
    }

    private updateState(state: IJoinCostCenterCostUnitState): void {
        this.store$.next(_state = state);
    }

    private setIsloading(isLoading: boolean): void {
        this.updateState({ ..._state, isLoading });
    }

    private setInputFields(references: IReference[], costCenters: ICostCenter[], costUnits: ICostUnit[], costAccountingTypes: ICostAccountingType[]): void {
        const selectFields: IField[] = [
            {
                field: 'selectedCostAccountingType',
                label: 'app.model.join-cost-center-cost-unit.costAccountingType.label',
                type: FieldType.select,
                selectData: {
                    displaySearch: true,
                    labelKeys: [ 'name' ],
                    valueKey: 'id',
                    options: of(costAccountingTypes),
                },
            },
            {
                field: 'selectedCostCenter',
                label: 'app.model.join-cost-center-cost-unit.costCenterId.label',
                type: FieldType.select,
                selectData: {
                    displaySearch: true,
                    labelKeys: [ 'name' ],
                    valueKey: 'id',
                    options: of(costCenters),
                },
            },
            {
                field: 'selectedDaterange',
                label: 'app.model.join-cost-center-cost-unit.daterange.label',
                type: FieldType.daterange,
            },
            {
                field: 'selectedDistributionReference',
                label: 'app.model.join-cost-center-cost-unit.distributionReference.label',
                type: FieldType.select,
                selectData: {
                    displaySearch: true,
                    labelKeys: [ 'name' ],
                    valueKey: 'id',
                    options: of(references),
                },
            },
        ];
        this.updateState({ ..._state, selectFields });

        const dialogAddFields: IField[] = [
            {
                field: 'costUnitId',
                label: 'app.model.join-cost-center-cost-unit.cost-unit.label',
                type: FieldType.select,
                required: true,
                selectData: {
                    displaySearch: true,
                    labelKeys: [ 'articleNr', 'name' ],
                    separator: ' - ',
                    valueKey: 'id',
                    options: of(costUnits),
                },
            },
            {
                field: 'costAccountingTypeId',
                label: 'app.model.join-cost-center-cost-unit.costAccountingType.label',
                type: FieldType.select,
                required: true,
                selectData: {
                    displaySearch: true,
                    labelKeys: [ 'name' ],
                    valueKey: 'id',
                    options: of(costAccountingTypes),
                },
            },
            {
                field: 'validFrom',
                label: 'app.model.join-cost-center-cost-unit.validFrom.label',
                type: FieldType.date,
                required: true,
            },
            {
                field: 'validTo',
                label: 'app.model.join-cost-center-cost-unit.validTo.label',
                type: FieldType.date,
                required: false,
            },

        ];

        const dialogEditFields: IField[] = [
            {
                field: 'validTo',
                label: 'app.model.join-cost-center-cost-unit.validTo.label',
                type: FieldType.date,
                required: true,
            },

        ];

        this.updateState({ ..._state, dialogAddFields });
        this.updateState({ ..._state, dialogEditFields });

    }

    private async fetchDistributionReference(): Promise<void> {
        const state: IJoinCostCenterCostUnitState = this.getStateSnapshot();
        if (!isNullOrUndefined(state.selectedCostCenter) && !isNullOrUndefined(state.selectedCostAccountingType)) {
            const distributionReference: IReference = await this.myCostCenterResource
                .getCostUnitDistributionReference(state.selectedCostCenter, state.selectedCostAccountingType).toPromise().catch(() => null);
            isNullOrUndefined(distributionReference)
                ? this.updateState({ ..._state, selectedDistributionReference: null })
                : this.updateState({ ..._state, selectedDistributionReference: distributionReference.id });
        } else {
            this.updateState({ ..._state, selectedDistributionReference: null });
        }
    }
}
